import React from 'react';
import Icon from 'components/AppIcon';

const UserDetailsModal = ({ user, onClose }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-success bg-success-50 border-success-200';
      case 'inactive': return 'text-secondary-600 bg-secondary-50 border-secondary-200';
      case 'pending': return 'text-warning bg-warning-50 border-warning-200';
      case 'suspended': return 'text-error bg-error-50 border-error-200';
      default: return 'text-secondary-600 bg-secondary-50 border-secondary-200';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'active': return 'Активен';
      case 'inactive': return 'Неактивен';
      case 'pending': return 'Ожидает активации';
      case 'suspended': return 'Заблокирован';
      default: return status;
    }
  };

  const formatDate = (date) => {
    if (!date) return 'Никогда';
    return new Intl.DateTimeFormat('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const getPermissionLabel = (permission) => {
    switch (permission) {
      case 'admin': return 'Администратор системы';
      case 'user_management': return 'Управление пользователями';
      case 'security_management': return 'Управление безопасностью';
      default: return permission;
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-surface rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-secondary-200">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center">
              <span className="text-lg font-medium text-white">
                {user.name.split(' ').map(n => n[0]).join('')}
              </span>
            </div>
            <div>
              <h2 className="text-xl font-semibold text-text-primary">
                {user.name}
              </h2>
              <p className="text-sm text-text-secondary">{user.email}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-md hover:bg-secondary-100 transition-colors duration-150"
          >
            <Icon name="X" size={20} className="text-secondary-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Status and Role */}
          <div className="flex items-center gap-4">
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(user.status)}`}>
              {getStatusText(user.status)}
            </span>
            <span className="inline-flex items-center px-3 py-1 bg-secondary-100 text-secondary-700 rounded-full text-sm font-medium">
              {user.role}
            </span>
          </div>

          {/* Personal Information */}
          <div>
            <h3 className="text-lg font-medium text-text-primary mb-4 flex items-center gap-2">
              <Icon name="User" size={18} />
              Личная информация
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium text-text-secondary">Email</label>
                  <p className="text-text-primary">{user.email}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-text-secondary">Телефон</label>
                  <p className="text-text-primary">{user.phone}</p>
                </div>
              </div>
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium text-text-secondary">Отдел</label>
                  <p className="text-text-primary">{user.department}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-text-secondary">Роль</label>
                  <p className="text-text-primary">{user.role}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Account Information */}
          <div>
            <h3 className="text-lg font-medium text-text-primary mb-4 flex items-center gap-2">
              <Icon name="Calendar" size={18} />
              Информация об аккаунте
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium text-text-secondary">Дата регистрации</label>
                  <p className="text-text-primary">{formatDate(user.registrationDate)}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-text-secondary">Последний вход</label>
                  <p className="text-text-primary">{formatDate(user.lastLogin)}</p>
                </div>
              </div>
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium text-text-secondary">Статус аккаунта</label>
                  <p className="text-text-primary">{getStatusText(user.status)}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-text-secondary">ID пользователя</label>
                  <p className="text-text-primary font-mono">#{user.id.toString().padStart(6, '0')}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Permissions */}
          <div>
            <h3 className="text-lg font-medium text-text-primary mb-4 flex items-center gap-2">
              <Icon name="Key" size={18} />
              Права доступа
            </h3>
            {user.permissions && user.permissions.length > 0 ? (
              <div className="space-y-2">
                {user.permissions.map(permission => (
                  <div key={permission} className="flex items-center gap-3 p-3 bg-secondary-50 rounded-md">
                    <Icon name="Check" size={16} className="text-success" />
                    <div>
                      <p className="font-medium text-text-primary">{getPermissionLabel(permission)}</p>
                      <p className="text-sm text-text-secondary">Код: {permission}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex items-center gap-3 p-4 bg-secondary-50 rounded-md">
                <Icon name="Info" size={16} className="text-secondary-500" />
                <p className="text-text-secondary">Базовые права пользователя</p>
              </div>
            )}
          </div>

          {/* Activity Summary */}
          <div>
            <h3 className="text-lg font-medium text-text-primary mb-4 flex items-center gap-2">
              <Icon name="Activity" size={18} />
              Активность
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center p-4 bg-secondary-50 rounded-md">
                <p className="text-2xl font-semibold text-text-primary">
                  {user.lastLogin ? Math.floor((Date.now() - user.lastLogin.getTime()) / (1000 * 60 * 60 * 24)) : 'N/A'}
                </p>
                <p className="text-sm text-text-secondary">Дней с последнего входа</p>
              </div>
              <div className="text-center p-4 bg-secondary-50 rounded-md">
                <p className="text-2xl font-semibold text-text-primary">
                  {Math.floor((Date.now() - user.registrationDate.getTime()) / (1000 * 60 * 60 * 24))}
                </p>
                <p className="text-sm text-text-secondary">Дней в системе</p>
              </div>
              <div className="text-center p-4 bg-secondary-50 rounded-md">
                <p className="text-2xl font-semibold text-text-primary">
                  {user.permissions?.length || 0}
                </p>
                <p className="text-sm text-text-secondary">Активных разрешений</p>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-secondary-200">
          <button
            onClick={onClose}
            className="
              px-4 py-2 border border-secondary-200 text-text-secondary rounded-md
              hover:bg-secondary-50 transition-colors duration-150
            "
          >
            Закрыть
          </button>
          <button
            onClick={() => {}}
            className="
              flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-md
              hover:bg-primary-700 transition-colors duration-150 hover-lift
            "
          >
            <Icon name="Edit" size={16} />
            Редактировать
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserDetailsModal;